import React, { useMemo, useState } from 'react';
import type { VguFaelligkeit } from '../../../services/vorsorgeService';
import { aggregateFaelligkeitenToKohorten, type Kohorte } from '../../../services/cohortsService';
import { TATBESTAND_MAP } from '../data/tatbestaende';

function KohorteCard({ k }: { k: Kohorte }): React.JSX.Element {
  const [showTermin, setShowTermin] = useState(false);
  const [terminDatum, setTerminDatum] = useState(k.vorschlagTermin);

  const tatbestand = TATBESTAND_MAP.get(k.tatbestandCode);
  const gesamt     = k.faellig + k.erledigt;
  const pctErledigt = gesamt > 0 ? Math.round((k.erledigt / gesamt) * 100) : 0;

  const statusFarbe = k.ueberfaellig > 0
    ? 'border-red-200 dark:border-red-800/50 bg-red-50/30 dark:bg-red-950/10'
    : k.faellig > 0
      ? 'border-orange-200 dark:border-orange-800/50 bg-orange-50/30 dark:bg-orange-950/10'
      : 'border-green-200 dark:border-green-800/50 bg-green-50/30 dark:bg-green-950/10';

  return (
    <div className={`card border ${statusFarbe} space-y-3`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400">{k.tatbestandCode}</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {tatbestand?.bezeichnung ?? k.tatbestandCode}
          </p>
          <p className="text-xs text-gray-500">{k.monthLabel}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className={`text-2xl font-bold ${k.faellig > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
            {k.faellig}
          </p>
          <p className="text-xs text-gray-400">fällig</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div>
          <p className="font-semibold text-orange-600 dark:text-orange-400">{k.faellig}</p>
          <p className="text-gray-400">Offen</p>
        </div>
        <div>
          <p className={`font-semibold ${k.ueberfaellig > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{k.ueberfaellig}</p>
          <p className="text-gray-400">Überfällig</p>
        </div>
        <div>
          <p className="font-semibold text-green-600 dark:text-green-400">{k.erledigt}</p>
          <p className="text-gray-400">Erledigt</p>
        </div>
      </div>

      {gesamt > 0 && (
        <div className="h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 rounded-full" style={{ width: `${pctErledigt}%` }} />
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{k.mitarbeiterKennungen.length} Mitarbeiter</span>
        {k.faellig > 1 && (
          <button onClick={() => { setShowTermin(v => !v); }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium">
            {showTermin ? 'Abbrechen' : '📅 Sammeltermin vorschlagen'}
          </button>
        )}
      </div>

      {showTermin && (
        <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-2">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Vorgeschlagener Termin</p>
          <div className="flex gap-2 items-center">
            <input type="date" value={terminDatum}
              onChange={e => { setTerminDatum(e.target.value); }}
              className="input-field text-sm flex-1" />
            <button
              onClick={() => {
                const text = `Sammeltermin: ${k.tatbestandCode} (${k.monthLabel}) — ${terminDatum} — ${k.faellig} Mitarbeiter`;
                if ('clipboard' in navigator) {
                  void navigator.clipboard.writeText(text);
                }
                setShowTermin(false);
              }}
              className="btn-primary text-xs px-3">
              📋 Kopieren
            </button>
          </div>
          <p className="text-xs text-gray-400">
            {k.mitarbeiterKennungen.slice(0, 5).join(', ')}{k.mitarbeiterKennungen.length > 5 ? ` +${k.mitarbeiterKennungen.length - 5} weitere` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

interface KohortenViewProps {
  faelligkeiten: VguFaelligkeit[];
}

export function KohortenView({ faelligkeiten }: KohortenViewProps): React.JSX.Element {
  const kohorten = useMemo(() => aggregateFaelligkeitenToKohorten(faelligkeiten), [faelligkeiten]);
  const nurOffen = kohorten.filter(k => k.faellig > 0);

  if (kohorten.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400">
        <p className="text-3xl mb-2">📅</p>
        <p>Keine Fälligkeiten vorhanden</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {nurOffen.length} Kohorten mit offenen Terminen · {kohorten.length} gesamt
        </p>
        <p className="text-xs text-gray-400">Gruppiert nach Monat + Tatbestand</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kohorten.map(k => (
          <KohorteCard key={`${k.month}::${k.tatbestandCode}`} k={k} />
        ))}
      </div>
    </div>
  );
}
